  import React from 'react';
import {
  Box,
  Typography,
  Avatar,
  Menu,
  MenuItem,
  Tooltip,
  ListItemIcon,
  ListItemText,
  List,
  ListItem,
  ListItemButton,
  IconButton,
} from '@mui/material';
import {
  Description as DescriptionIcon,
  Assignment as AssignmentIcon,
  Settings as SettingsIcon,
  AccountCircle as AccountCircleIcon,
  Logout as LogoutIcon,
  Business as BusinessIcon,
  Folder as FolderIcon,
  Chat as ChatIcon,
  Menu as MenuIcon,
  Sync as SyncIcon,
  SwapHoriz as SwapHorizIcon,
} from '@mui/icons-material';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { UserRole } from '../types';
import { User as AppUser } from '../types';
import ThemeToggle from '../components/ThemeToggle';
import './Sidebar.css';

interface SidebarProps {
  open: boolean;
  isMobile: boolean;
  handleDrawerToggle: () => void;
  handleMobileDrawerClose?: () => void;
  isPinned: boolean; // New prop
  handlePinToggle: () => void; // New prop
}

const Sidebar: React.FC<SidebarProps> = ({
  open,
  isMobile,
  handleDrawerToggle,
  handleMobileDrawerClose,
  isPinned, // Destructure new prop
  handlePinToggle, // Destructure new prop
}) => {
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const [orgMenuAnchorEl, setOrgMenuAnchorEl] = React.useState<null | HTMLElement>(null);
  const [isLoading, setIsLoading] = React.useState(false);
  const [justClicked, setJustClicked] = React.useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout, switchOrganization, organizations, currentOrganization } = useAuth();
  const { isDark } = useTheme();

  const defaultUser: AppUser = {
    id: '0',
    name: 'Guest User',
    email: 'guest@example.com',
    role: UserRole.Viewer,
  };

  const appUser: AppUser = user ? {
    id: user.id,
    name: user.email.split('@')[0],
    email: user.email,
    role: currentOrganization ? currentOrganization.organization_role : UserRole.Viewer,
  } : defaultUser

  const handleMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleOrgMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setOrgMenuAnchorEl(event.currentTarget);
  };

  const handleOrgMenuClose = () => {
    setOrgMenuAnchorEl(null);
  };

  const handleProfile = () => {
    handleClose();
    navigate('/profile');
  };

  const handleLogout = () => {
    handleClose();
    logout();
  };

  const isSuperUser = currentOrganization && (currentOrganization.organization_role === 'Admin' || currentOrganization.organization_role === 'Owner');

  const getNavigationItems = () => {
    const baseItems = [
      { text: 'Projects', icon: <FolderIcon />, path: '/projects' },
      { text: 'Document Workspace', icon: <DescriptionIcon />, path: '/' },
      { text: 'Chat', icon: <ChatIcon />, path: '/chat' },
    ];

    const conditionalItems = [];
    const userPermissions = user?.permissions?.map(p => p.name) || [];

    if (userPermissions.includes('ddq')) {
      conditionalItems.push({ text: 'DDQ', icon: <DescriptionIcon />, path: '/ddq' });
    } if (userPermissions.includes('cim')) {
      conditionalItems.push(
        { text: 'Investment Thesis', icon: <AssignmentIcon />, path: '/investment-thesis' },
        { text: 'Deal Analysis', icon: <BusinessIcon />, path: '/deal-sourcing' }
      );
    }

    const adminAndIntegrationItems = [
      ...(isSuperUser ? [{ text: 'Admin', icon: <SettingsIcon />, path: '/admin' }] : []),
      { text: 'Integrations', icon: <SyncIcon />, path: '/integrations' },
    ];

    return [...baseItems, ...conditionalItems, ...adminAndIntegrationItems];
  };

  const navigationItems = getNavigationItems();

  const handleNavigation = (path: string) => {
    setIsLoading(true);
    setJustClicked(true);
    navigate(path);
    if (isMobile && handleMobileDrawerClose) {
      handleMobileDrawerClose();
    }
    // Simulate loading state
    setTimeout(() => setIsLoading(false), 300);
    setTimeout(() => setJustClicked(false), 500);
  };

  return (
    <Box
      className={`sidebar ${!open && 'collapsed'} ${isLoading && 'loading'} ${isPinned ? 'pinned' : ''}`}
      onMouseEnter={() => !isMobile && !open && !isPinned && handleDrawerToggle()}
      onMouseLeave={() => !isMobile && open && !justClicked && !isPinned && handleDrawerToggle()}
    >
      <Box className="sidebar-header">
        <Typography
          variant="h6"
          component={Link}
          to="/"
          className="sidebar-logo"
          sx={{
            display: 'flex',
            alignItems: 'center',
            textDecoration: 'none',
            color: 'text.primary',
            justifyContent: (!open && !isMobile) ? 'center' : 'flex-start',
            width: (!open && !isMobile) ? '100%' : 'auto',
          }}
        >
          <span className="logo-icon">A</span>
          {(open || isMobile) && 'AnCore'}
        </Typography>
        {(open || isMobile) && ( // Show pin icon only when sidebar is open
          <Tooltip title={isPinned ? "Unpin Sidebar" : "Pin Sidebar"} placement="right">
            <IconButton onClick={handlePinToggle} sx={{ ml: 5 }}>
              <MenuIcon color={isPinned ? 'primary' : 'action'} />
            </IconButton>
          </Tooltip>
        )}
      </Box>
      
      <List component="nav" className="sidebar-nav" sx={{ px: (open || isMobile) ? 2 : 1, flexGrow: 1, overflowY: 'auto' }}>
        {navigationItems.map((item, index) => {
          const isActive = item.path === '/projects'
            ? location.pathname.startsWith(item.path)
            : item.path === '/deal-sourcing'
            ? location.pathname.startsWith('/deals/') || location.pathname === item.path
            : location.pathname === item.path;
          return (
            <ListItem
              key={item.text}
              disablePadding
              sx={{
                display: 'block',
                mb: 1,
                animationDelay: `${index * 0.05}s`,
              }}
            >
              <Tooltip title={(!open && !isMobile) ? item.text : ''} placement="right">
                <ListItemButton
                  onClick={() => handleNavigation(item.path)}
                  sx={{
                    minHeight: 48,
                    justifyContent: (open || isMobile) ? 'initial' : 'center',
                    px: (open || isMobile) ? 1.5 : 2,
                    borderRadius: '12px',
                    backgroundColor: isActive ? 'primary.main' : 'transparent',
                    color: isActive ? 'primary.contrastText' : 'text.secondary',
                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                    position: 'relative',
                    overflow: 'hidden',
                    '&::before': {
                      content: '""',
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      background: 'linear-gradient(45deg, transparent, rgba(255,255,255,0.1), transparent)',
                      transform: 'translateX(-100%)',
                      transition: 'transform 0.6s ease',
                    },
                    '&:hover': {
                      backgroundColor: isActive ? 'primary.dark' : 'action.hover',
                      color: isActive ? 'primary.contrastText' : 'text.primary',
                      transform: 'translateX(4px)',
                      boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                      '&::before': {
                        transform: 'translateX(100%)',
                      },
                    },
                    '&:active': {
                      transform: 'translateX(2px) scale(0.98)',
                    },
                  }}
                >
                  <ListItemIcon
                    className="nav-icon"
                    sx={{
                      minWidth: 0,
                      mr: (open || isMobile) ? 3 : 0,
                      justifyContent: 'center',
                      color: 'inherit',
                      transition: 'all 0.3s ease',
                    }}
                  >
                    {item.icon}
                  </ListItemIcon>
                  <ListItemText
                    primary={item.text}
                    sx={{
                      opacity: (open || isMobile) ? 1 : 0,
                      width: (open || isMobile) ? 'auto' : 0,
                      color: 'inherit',
                      transition: 'opacity 0.3s ease, width 0.3s ease',
                      whiteSpace: 'nowrap',
                    }}
                    primaryTypographyProps={{
                      style: {
                        fontSize: '0.875rem',
                        fontWeight: isActive ? 600 : 400,
                      }
                    }}
                  />
                </ListItemButton>
              </Tooltip>
            </ListItem>
          );
        })}
      </List>
      
      <Box className="sidebar-footer">
        <Box sx={{ px: (open || isMobile) ? 2 : 0.5, my: 1 }}>
          <ThemeToggle variant="menu" showLabel={open || isMobile} />
        </Box>
        <Box sx={{ display: 'flex', justifyContent: 'center' }}> {/* Reverted to original flex properties */}
          <Tooltip title={!open && !isMobile ? appUser.name : ''} placement="right">
            <button
              onClick={handleMenu}
              style={{
                width: open || isMobile ? '100%' : 'auto',
                marginTop: '1rem',
                padding: open || isMobile ? '0.75rem 1rem' : '0.5rem',
                background: 'var(--surface-color)',
                borderRadius: 'var(--radius-md)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: open || isMobile ? 'flex-start' : 'center',
                border: 'none',
                cursor: 'pointer',
                transition: 'all var(--transition-normal)',
              }}
            >
              <Avatar
                alt={appUser.name}
                src={appUser.avatar}
                sx={{
                  width: 32,
                  height: 32,
                  mr: (open || isMobile) ? 1 : 0,
                  transition: 'all 0.3s ease',
                  bgcolor: isDark ? 'grey.300' : 'primary.main',
                  color: isDark ? 'black' : 'white',
                }}
              />
              <Box
                sx={{
                  flex: 1,
                  textAlign: 'left',
                  opacity: open || isMobile ? 1 : 0,
                  width: open || isMobile ? 'auto' : 0,
                  transition: 'opacity 0.3s ease, width 0.3s ease',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                }}
              >
                <Typography variant="body2" sx={{ fontWeight: 500, color: isDark ? '#FFFFFF' : 'var(--text-primary)' }}>
                  {appUser.name}
                </Typography>
                {currentOrganization && (
                  <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                    {currentOrganization.name}
                  </Typography>
                )}
              </Box>
              {(open || isMobile) && organizations.length > 1 && (
                <Tooltip title="Switch Organization" placement="right">
                  <IconButton onClick={(e) => { e.stopPropagation(); handleOrgMenuOpen(e); }} sx={{ ml: 1 }}>
                    <SwapHorizIcon />
                  </IconButton>
                </Tooltip>
              )}
            </button>
          </Tooltip>
          {organizations.length > 1 && (
            <Menu
              anchorEl={orgMenuAnchorEl}
              open={Boolean(orgMenuAnchorEl)}
              onClose={handleOrgMenuClose}
            >
              <Typography variant="caption" sx={{ px: 2, py: 1, color: 'text.secondary' }}>
                Switch Organization
              </Typography>
              {organizations.map((org) => (
                <MenuItem key={org.id} onClick={() => switchOrganization(org)}>
                  <ListItemIcon>
                    <BusinessIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText>{org.name}</ListItemText>
                </MenuItem>
              ))}
            </Menu>
          )}
          <Menu
            id="menu-appbar"
            anchorEl={anchorEl}
            anchorOrigin={{
              vertical: 'top',
              horizontal: 'right',
            }}
            keepMounted
            transformOrigin={{
              vertical: 'bottom',
              horizontal: 'right',
            }}
            open={Boolean(anchorEl)}
            onClose={handleClose}
          >
            <MenuItem onClick={handleProfile}>
              <ListItemIcon sx={{ color: isDark ? '#FFFFFF' : 'inherit' }}>
                <AccountCircleIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText sx={{ color: isDark ? '#FFFFFF' : 'inherit' }}>Profile</ListItemText>
            </MenuItem>
            <MenuItem onClick={handleLogout}>
              <ListItemIcon sx={{ color: isDark ? '#FFFFFF' : 'inherit' }}>
                <LogoutIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText sx={{ color: isDark ? '#FFFFFF' : 'inherit' }}>Logout</ListItemText>
            </MenuItem>
          </Menu>
        </Box>
      </Box>
    </Box>
  );
};

export default Sidebar;
